/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Case, Switch } from "react-if";
import {
  ChartType,
  chartTypeOptions,
  CostAnalysisChartHeader,
  DateRange,
  useCostAnalysis,
  usePeriod,
} from "~/components";
import { APP_ENV } from "~/config";
import { getFirstItem } from "~/tools";
import {
  ApplicationAccumulatedCostView,
  ApplicationAnomalyCostView,
  ApplicationCostPerEnvironmentView,
  ApplicationCostPerCategoryView,
  ApplicationCostVersusBudgetView,
} from "./views";

type Props = {
  appId: string;
  forecast: number;
};

export function ApplicationCostAnalysisSection({ appId, forecast }: Props) {
  const [range, setRange] = useState<DateRange | undefined>(undefined);

  const {
    period,
    granularity,
    setGranularity,
    startDate,
    endDate,
    setPeriod,
    isDailyChartViewDisabled,
  } = usePeriod(range);

  const chartOptions = chartTypeOptions.filter(({ value }) =>
    showAnomalyCostChart(value)
  );

  const { chartType, setChartType, hasFooter, isChartVisible, showCostTable } =
    useCostAnalysis({ period, chartOptions });

  const defaultOption = getFirstItem(chartOptions);

  return (
    <>
      <CostAnalysisChartHeader
        period={period}
        options={chartOptions}
        chartType={chartType}
        range={range}
        granularity={granularity}
        disableDailyView={isDailyChartViewDisabled}
        onHandleClick={setPeriod}
        onSetRange={setRange}
        onSetGranularity={setGranularity}
        onChange={(item) => {
          setChartType(item ?? defaultOption);
        }}
      />
      <Switch>
        <Case condition={isChartVisible(ChartType.ACCUMULATED_COST)}>
          <ApplicationAccumulatedCostView
            appId={appId}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.ANOMALY_COST)}>
          <ApplicationAnomalyCostView
            appId={appId}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_PER_ENVIRONMENT)}>
          <ApplicationCostPerEnvironmentView
            appId={appId}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_PER_CATEGORY)}>
          <ApplicationCostPerCategoryView
            appId={appId}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_VS_BUDGET)}>
          <ApplicationCostVersusBudgetView
            appId={appId}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
            forecast={forecast}
          />
        </Case>
      </Switch>
    </>
  );
}

function showAnomalyCostChart(chartType: string): boolean {
  if (chartType !== ChartType.ANOMALY_COST) {
    return true;
  }

  // Excluding anomaly cost chart from UK and US regions as these regions are missing anomaly service.
  const environmentsToExclude = [
    "production-uk",
    "production-us",
    "eh-production",
  ];

  return !environmentsToExclude.includes(APP_ENV);
}
